import { lazy, Suspense, useEffect, useLayoutEffect } from "react";
import { rememberClick } from "@/lib/attribution";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { LocaleProvider } from "@/context/LocaleContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import FeaturePage from "@/pages/features/FeaturePage";
import Auth from "@/pages/Auth";
import Shop from "@/pages/Shop";
import Deals from "@/pages/Deals";
import ProductDetail from "@/pages/ProductDetail";
import Feed from "@/pages/Feed";
import TryOn from "@/pages/TryOn";
import Cart from "@/pages/Cart";
import Orders from "@/pages/Orders";
import Profile from "@/pages/Profile";
import Live from "@/pages/Live";
import LiveRoom from "@/pages/LiveRoom";
import Looks from "@/pages/Looks";
import About from "@/pages/About";
import WidgetDemo from "@/pages/WidgetDemo";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import AcceptableUse from "@/pages/legal/AcceptableUse";
import Policies from "@/pages/legal/Policies";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PaymentCashfree from "@/pages/PaymentCashfree";
import SellerDashboard from "@/pages/seller/Dashboard";
import AddProduct from "@/pages/seller/AddProduct";
import Photoshoot from "@/pages/seller/Photoshoot";
import MyProducts from "@/pages/seller/MyProducts";
import GoLive from "@/pages/seller/GoLive";
import AiApps from "@/pages/seller/AiApps";
import DevelopersLanding from "@/pages/developers/DevelopersLanding";
import Crosslist from "@/pages/seller/Crosslist";
import ItemForm from "@/pages/seller/ItemForm";
import Labels from "@/pages/seller/Labels";
import ImportInventory from "@/pages/seller/ImportInventory";
import Analytics from "@/pages/seller/Analytics";
import Templates from "@/pages/seller/Templates";
import ExportCsv from "@/pages/seller/ExportCsv";
import Connections from "@/pages/seller/Connections";
import Extension from "@/pages/seller/Extension";
import Automations from "@/pages/seller/Automations";
import Offers from "@/pages/seller/Offers";
import Onboarding from "@/pages/seller/Onboarding";
import SellerOrders from "@/pages/seller/SellerOrders";
import Admin from "@/pages/admin/Admin";
import { needsOnboarding, needsBilling } from "@/lib/onboarding";
import Subscribe from "@/pages/seller/Subscribe";

const DeveloperConsole = lazy(() => import("@/pages/developers/Console"));
const loadingScreen = <div className="min-h-screen flex items-center justify-center text-aura-muted">Loading…</div>;

function ScrollToTop() {
  const { pathname, search } = useLocation();
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
  }, [pathname]);
  useEffect(() => {
    rememberClick(search);
  }, [search]);
  return null;
}

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-aura-muted">Loading…</div>;
  if (!user) return <Navigate to="/auth?mode=login" replace />;
  if (role && user.role !== role && user.role !== "admin") {
    if (user.impersonated_by && user.role === "seller") {
      return <Navigate to="/seller" replace />;
    }
    return <Navigate to="/shop" replace />;
  }
  if (role === "seller" && needsBilling(user) && location.pathname !== "/seller/subscribe" && !location.pathname.startsWith("/payment")) {
    return <Navigate to="/seller/subscribe" replace />;
  }
  if (role === "seller" && needsOnboarding(user) && location.pathname === "/seller") {
    return <Navigate to="/seller/onboarding" replace />;
  }
  return children;
}

function DeveloperRoute({ children, requireLogin = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-aura-muted">Loading…</div>;
  if (user?.role === "seller") return <Navigate to="/seller" replace />;
  if (requireLogin && !user) return <Navigate to="/auth?mode=login" replace />;
  if (requireLogin && user && user.role !== "developer" && user.role !== "admin") {
    return <Navigate to="/developers" replace />;
  }
  return children;
}

function LegacyDevelopers() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.role === "seller") return <Navigate to="/seller/ai-apps" replace />;
  return <Navigate to={user ? "/developers/console" : "/developers"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <LocaleProvider>
      <WishlistProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/features/:slug" element={<FeaturePage />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/deals" element={<Deals />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/live" element={<Live />} />
            <Route path="/live/:id" element={<LiveRoom />} />
            <Route path="/about" element={<About />} />
            <Route path="/widget-demo" element={<WidgetDemo />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/acceptable-use" element={<AcceptableUse />} />
            <Route path="/policies" element={<Policies />} />
            <Route path="/returns" element={<Policies />} />
            <Route path="/refunds" element={<Policies />} />
            <Route path="/cancellations" element={<Policies />} />
            <Route path="/feed" element={<Protected><Feed /></Protected>} />
            <Route path="/tryon" element={<Protected><TryOn /></Protected>} />
            <Route path="/looks" element={<Protected><Looks /></Protected>} />
            <Route path="/cart" element={<Protected><Cart /></Protected>} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/payment/cashfree" element={<Protected><PaymentCashfree /></Protected>} />
            <Route path="/orders" element={<Protected><Orders /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/seller" element={<Protected role="seller"><SellerDashboard /></Protected>} />
            <Route path="/seller/subscribe" element={<Protected role="seller"><Subscribe /></Protected>} />
            <Route path="/seller/onboarding" element={<Protected role="seller"><Onboarding /></Protected>} />
            <Route path="/seller/extension" element={<Protected role="seller"><Extension /></Protected>} />
            <Route path="/seller/add" element={<Protected role="seller"><AddProduct /></Protected>} />
            <Route path="/seller/photoshoot" element={<Protected role="seller"><Photoshoot /></Protected>} />
            <Route path="/seller/live" element={<Protected role="seller"><GoLive /></Protected>} />
            <Route path="/seller/products" element={<Protected role="seller"><MyProducts /></Protected>} />
            <Route path="/seller/crosslist" element={<Protected role="seller"><Crosslist /></Protected>} />
            <Route path="/seller/crosslist/labels" element={<Protected role="seller"><Labels /></Protected>} />
            <Route path="/seller/crosslist/import" element={<Protected role="seller"><ImportInventory /></Protected>} />
            <Route path="/seller/crosslist/analytics" element={<Protected role="seller"><Analytics /></Protected>} />
            <Route path="/seller/crosslist/templates" element={<Protected role="seller"><Templates /></Protected>} />
            <Route path="/seller/crosslist/export" element={<Protected role="seller"><ExportCsv /></Protected>} />
            <Route path="/seller/crosslist/:id" element={<Protected role="seller"><ItemForm /></Protected>} />
            <Route path="/seller/connections" element={<Protected role="seller"><Connections /></Protected>} />
            <Route path="/seller/connections/:channel" element={<Protected role="seller"><Connections /></Protected>} />
            <Route path="/seller/automations" element={<Protected role="seller"><Automations /></Protected>} />
            <Route path="/seller/automations/auto-offers" element={<Protected role="seller"><Automations /></Protected>} />
            <Route path="/seller/automations/sharing" element={<Protected role="seller"><Automations /></Protected>} />
            <Route path="/seller/automations/stale" element={<Protected role="seller"><Automations /></Protected>} />
            <Route path="/seller/offers" element={<Protected role="seller"><Offers /></Protected>} />
            <Route path="/seller/orders" element={<Protected role="seller"><SellerOrders /></Protected>} />
            <Route path="/seller/ai-apps" element={<Protected role="seller"><AiApps /></Protected>} />
            <Route path="/seller/developers" element={<LegacyDevelopers />} />
            <Route path="/developers" element={<DeveloperRoute><DevelopersLanding /></DeveloperRoute>} />
            <Route path="/developers/console" element={<DeveloperRoute requireLogin><Suspense fallback={loadingScreen}><DeveloperConsole /></Suspense></DeveloperRoute>} />
            <Route path="/developers/console/brands/:brandId" element={<DeveloperRoute requireLogin><Suspense fallback={loadingScreen}><DeveloperConsole /></Suspense></DeveloperRoute>} />
            <Route path="/developers/console/categories/:categoryId" element={<DeveloperRoute requireLogin><Suspense fallback={loadingScreen}><DeveloperConsole /></Suspense></DeveloperRoute>} />
            <Route path="/developers/console/:section" element={<DeveloperRoute requireLogin><Suspense fallback={loadingScreen}><DeveloperConsole /></Suspense></DeveloperRoute>} />
            <Route path="/admin" element={<Protected role="admin"><Admin /></Protected>} />
          </Route>
        </Routes>
      </BrowserRouter>
      </WishlistProvider>
      </LocaleProvider>
      <Toaster position="bottom-right" theme="dark" />
    </AuthProvider>
  );
}

export default App;
